/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Authentication Zod Validation Schemas & Data Transfer Objects
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Strong password validation regex:
 * At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special symbol.
 */
const passwordValidation = z
  .string()
  .min(8, 'Password must be at least 8 characters long')
  .max(128, 'Password must not exceed 128 characters')
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
  )
  .openapi({
    description: 'Strong password with min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char',
    example: 'SophiaSecure2026!#',
  });

/**
 * User registration request schema.
 */
export const RegisterSchema = z
  .object({
    email: z
      .string()
      .trim()
      .email('Invalid email address format')
      .toLowerCase()
      .openapi({ description: 'User unique email address', example: 'developer@sophiaplatform.com' }),
    password: passwordValidation,
    fullName: z
      .string()
      .trim()
      .min(2, 'Full name must be at least 2 characters long')
      .max(100)
      .openapi({ description: 'Full legal or display name', example: 'Le Viet Quang' }),
    workspaceName: z
      .string()
      .trim()
      .min(2)
      .max(100)
      .optional()
      .openapi({ description: 'Optional custom name for initial workspace', example: 'Four Elements Studio' }),
  })
  .openapi('RegisterRequest');

export type RegisterDto = z.infer<typeof RegisterSchema>;

/**
 * User login request schema.
 */
export const LoginSchema = z
  .object({
    email: z
      .string()
      .trim()
      .email('Invalid email address format')
      .toLowerCase()
      .openapi({ description: 'User account email', example: 'developer@sophiaplatform.com' }),
    password: z.string().min(1, 'Password is required').openapi({ description: 'Account password', example: 'SophiaSecure2026!#' }),
  })
  .openapi('LoginRequest');

export type LoginDto = z.infer<typeof LoginSchema>;

/**
 * Refresh token rotation request schema.
 */
export const RefreshTokenSchema = z
  .object({
    refreshToken: z.string().min(1, 'Refresh token is required').openapi({
      description: 'Long-lived JWT refresh token',
      example: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
    }),
  })
  .openapi('RefreshTokenRequest');

export type RefreshTokenDto = z.infer<typeof RefreshTokenSchema>;

/**
 * Password reset request schema.
 */
export const ResetPasswordRequestSchema = z
  .object({
    email: z
      .string()
      .trim()
      .email('Invalid email address format')
      .toLowerCase()
      .openapi({ description: 'Registered email to receive password reset link', example: 'developer@sophiaplatform.com' }),
  })
  .openapi('ResetPasswordRequest');

export type ResetPasswordRequestDto = z.infer<typeof ResetPasswordRequestSchema>;

/**
 * Confirm password reset schema with verification token.
 */
export const ResetPasswordConfirmSchema = z
  .object({
    token: z.string().min(1, 'Verification token is required').openapi({ description: 'Password reset token from email', example: 'tok_reset_abc123' }),
    newPassword: passwordValidation,
  })
  .openapi('ResetPasswordConfirmRequest');

export type ResetPasswordConfirmDto = z.infer<typeof ResetPasswordConfirmSchema>;

/**
 * Change password schema for authenticated users.
 */
export const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required').openapi({ description: 'Current user password', example: 'OldPassword2025!#' }),
    newPassword: passwordValidation,
  })
  .openapi('ChangePasswordRequest');

export type ChangePasswordDto = z.infer<typeof ChangePasswordSchema>;

/**
 * Two-Factor Authentication (TOTP) verification schema.
 */
export const TwoFactorVerifySchema = z
  .object({
    code: z
      .string()
      .trim()
      .length(6, '2FA code must be exactly 6 digits')
      .regex(/^\d{6}$/, '2FA code must contain only numeric digits')
      .openapi({ description: '6-digit TOTP code from authenticator app', example: '123456' }),
  })
  .openapi('TwoFactorVerifyRequest');

export type TwoFactorVerifyDto = z.infer<typeof TwoFactorVerifySchema>;

/**
 * Authentication tokens response schema.
 */
export const AuthTokensResponseSchema = z
  .object({
    accessToken: z.string().openapi({ description: 'Short-lived JWT Access Token (15m)', example: 'eyJhbGciOi...' }),
    refreshToken: z.string().openapi({ description: 'Long-lived JWT Refresh Token (30d)', example: 'eyJhbGciOi...' }),
    expiresIn: z.number().openapi({ description: 'Access token expiration in seconds', example: 900 }),
    user: z.object({
      id: z.string().openapi({ description: 'User unique ID', example: 'usr_01HXYZ789' }),
      email: z.string().openapi({ description: 'User email', example: 'developer@sophiaplatform.com' }),
      fullName: z.string().openapi({ description: 'User full name', example: 'Le Viet Quang' }),
      role: z.string().openapi({ description: 'Role in system', example: 'USER' }),
      workspaceId: z.string().openapi({ description: 'Active workspace ID', example: 'ws_01HXYZ123' }),
    }),
  })
  .openapi('AuthTokensResponse');

